<template>
    <div class="container mt-7 pb-5">
        <div class="row justify-content-center">
            <div class="col-lg-5 col-md-8">
                <div class="card bg-secondary border-0 mb-0">
                    <div class="card-body px-lg-5 py-lg-5 text-center">
                        <!-- Absolute: this page lives at /auth/line/callback, so a relative "img/logo.png"
                             resolved to /auth/line/img/logo.png — the SPA fallback answered with
                             index.html and the logo never rendered (owner, bo prod 2026-10-07). -->
                        <img src="/img/logo.png" class="line-callback__logo" />
                        <!-- CSS spinner, not `fas fa-spin`: FontAwesome is a
                             CDN <link> (public/index.html:30) and a blocked CDN
                             would leave this page completely blank. -->
                        <div class="mt-4">
                            <span class="line-callback__spinner" aria-hidden="true"></span>
                        </div>
                        <p class="mt-3 mb-0 text-muted" role="status" aria-live="polite">
                            {{ $t("auth.line_connecting") }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
// /auth/line/callback — the page LINE redirects back to.
//
// Spec: docs/specs/2026-10-03-bo-staff-line-login-design.md §1, §2, §5, §6.
//
// It renders a spinner and nothing else: everything it does happens in
// mounted(), and every exit path leaves this route (login → "/", link →
// "/profile/me", any failure → "/login" or "/profile/me" with a swal). There is
// no state in the template on purpose — a user who lands here with a consumed
// state must not be able to sit on a screen with a "retry" button that cannot
// work, because the `code` is single-use at LINE too.
import swal from "sweetalert2";
import { mapActions } from "vuex";
import { setAuthCookie } from "@/util/auth-cookie";
import {
    LINE_PURPOSE_LINK,
    LINE_PURPOSE_LOGIN,
    forgetLinePurpose,
    lineCallbackParams,
    lineErrorKeyFromError,
    lineResolvePurpose,
    readLinePurpose,
    staffSessionUser
} from "@/utils/boLine";

export default {
    data() {
        return {
            // Double-run guard. mounted() can fire twice in development with
            // hot reload, and a second POST with the same `code` gets a 502/400
            // from LINE after the first one already succeeded — i.e. a
            // successful link that reports failure. Same reason mdf-www's
            // LineCallback.tsx keeps a `ran` ref.
            ran: false
        };
    },
    methods: {
        ...mapActions({
            lineCallback: "Auth/lineCallback",
            lineLink: "Auth/lineLink",
            setUser: "Auth/setUser",
            getProfile: "Auth/getProfile",
            setProfile: "Auth/setProfile"
        }),

        // fail shows one message and leaves. `where` is the route to land on so
        // the user is never stranded on this page.
        fail(key, where) {
            forgetLinePurpose();
            swal.fire({
                title: this.$t(key),
                text: ``,
                size: `sm`,
                icon: "error",
                confirmButtonClass: "btn btn-danger btn-fill",
                buttonsStyling: false
            });
            this.$router.replace(where);
        },

        async run() {
            // $route.query on a normal navigation; window.location.search as
            // the fallback for a cold load where the router has not populated
            // the query yet.
            const raw =
                this.$route && this.$route.query && Object.keys(this.$route.query).length
                    ? this.$route.query
                    : (typeof window !== "undefined" && window.location && window.location.search) || "";
            const params = lineCallbackParams(raw);
            const purpose = lineResolvePurpose(readLinePurpose(), params.purpose);
            const home = purpose === LINE_PURPOSE_LINK ? "/profile/me" : "/login";

            // LINE answers ?error=access_denied when the user presses "ยกเลิก"
            // on the consent screen. That is not a failure to report to the API
            // (spec §6 ข้อ 7) — there is no code to exchange, and posting an
            // empty one would only burn the state.
            if (params.error) {
                this.fail("errors.auth.line_cancelled", home);
                return;
            }

            // No code / no state means a bookmarked or hand-typed callback URL.
            // Stopped here rather than at the API so a refresh of this page
            // does not count against the per-IP cap.
            if (!params.code || !params.state) {
                this.fail("errors.auth.line_state_invalid", home);
                return;
            }

            // Only `code` and `state` go to the API, and only in the body —
            // never logged, never put back in a URL (spec §1).
            const body = { code: params.code, state: params.state };

            if (purpose === LINE_PURPOSE_LINK) {
                try {
                    await this.lineLink(body);
                    forgetLinePurpose();
                    // Re-read the profile so the card renders from the same
                    // source of truth it uses on a normal page load, rather
                    // than from the link response only.
                    try {
                        const prof = await this.getProfile();
                        if (prof && prof.data && prof.data.Datum) {
                            this.setProfile(prof.data.Datum);
                        }
                    } catch (e) {
                        // The link itself succeeded; a failed refetch must not
                        // be reported as a failed link.
                    }
                    swal.fire({
                        title: this.$t("profile.line_link_success"),
                        text: ``,
                        size: `sm`,
                        icon: "success",
                        confirmButtonClass: "btn btn-default btn-fill",
                        buttonsStyling: false
                    });
                    this.$router.replace("/profile/me");
                } catch (err) {
                    this.fail(lineErrorKeyFromError(err), "/profile/me");
                }
                return;
            }

            // purpose === login
            try {
                const res = await this.lineCallback(body);
                forgetLinePurpose();
                // Identical hand-off to the password path (Login.vue onSubmit):
                // same cookie helper, same two config-derived flags lifted out
                // of the body beside User, same landing route. staffSessionUser
                // is shared so the flags cannot drift apart
                // (spec §1 "logic ซ้ำกันห้ามแตกต่าง").
                setAuthCookie(this.$cookie, res.data.Token);
                this.setUser(staffSessionUser(res.data));
                this.$router.push("/");
            } catch (err) {
                this.fail(lineErrorKeyFromError(err), "/login");
            }
        }
    },
    mounted() {
        if (this.ran) return;
        this.ran = true;
        this.run();
    },
    beforeDestroy() {
        // Belt and braces: every exit path above already clears it, but an exit
        // by browser back would otherwise leave a stale purpose behind for the
        // next flow to pick up.
        forgetLinePurpose();
    }
};
</script>
<style scoped>
.line-callback__logo {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
}

.line-callback__spinner {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    border: 3px solid #dee2e6;
    border-top-color: #06c755;
    border-radius: 50%;
    animation: line-callback-spin 0.8s linear infinite;
}

@keyframes line-callback-spin {
    to {
        transform: rotate(360deg);
    }
}

/* Respect the OS "reduce motion" setting — a spinning ring is decoration here,
   the sentence underneath carries the message. */
@media (prefers-reduced-motion: reduce) {
    .line-callback__spinner {
        animation: none;
    }
}
</style>
